/**
 * Mazaneh («مظنه»: price of one mesghal, 4.608 g, of 705/1000 gold) — formulas and Persian formatting.
 * Safe for both server and client.
 */

export const MESGHAL_GRAMS = 4.608;
export const MAZANEH_PURITY = 705;
/** Market rule of thumb: one gram of 18k = mazaneh ÷ 4.33 (the article uses this number). */
export const GRAM18_DIVISOR = 4.33;
/** Theoretical mazaneh = ounce (USD) × dollar (toman) ÷ 9.5742. */
export const OUNCE_DIVISOR = 9.5742;

export const TEHRAN_TZ = 'Asia/Tehran';
/** Tehran has had no daylight saving since 1401, so it is always UTC+3:30. */
export const TEHRAN_OFFSET_SEC = 3.5 * 3600;

/** Tehran gold market hours (Saturday–Thursday 10:00–20:00), for the «بازار بسته» label. Days use getUTCDay (Friday = 5). */
export const MARKET_HOURS = { open: 10, close: 20, closedDays: [5] };
/** A price older than this is shown as «آخرین نرخ». */
export const STALE_AFTER_MS = 10 * 60 * 1000;

export const gramPrice = (mazaneh: number, purity: number): number =>
    purity === 750
        ? mazaneh / GRAM18_DIVISOR
        : (mazaneh / MESGHAL_GRAMS) * (purity / MAZANEH_PURITY);

export const theoreticalMazaneh = (ounceUsd: number, dollarToman: number): number =>
    (ounceUsd * dollarToman) / OUNCE_DIVISOR;

export const roundThousand = (n: number): number => Math.round(n / 1000) * 1000;

const tomanFmt = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat('fa-IR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const millionFmt = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 1 });

/** 114500000 → «۱۱۴٬۵۰۰٬۰۰۰» */
export const faNumber = (n: number): string => tomanFmt.format(n);
/** 0.96 → «۰٫۹۶» (no sign) */
export const faPercent = (n: number): string => pctFmt.format(Math.abs(n));
/** 114500000 → «۱۱۴٫۵» */
const usdFmt = new Intl.NumberFormat('fa-IR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** 4048.47 → «۴٬۰۴۸٫۴۷» */
export const faUsd = (n: number): string => usdFmt.format(n);
export const faMillion = (n: number): string => millionFmt.format(n / 1_000_000);
/** +1080000 → «+۱٬۰۸۰٬۰۰۰» */
export const faSigned = (n: number): string =>
    `${n > 0 ? '+' : n < 0 ? '−' : ''}${faNumber(Math.abs(n))}`;

/** Reads «۱۱۴٬۰۰۰٬۰۰۰», «114,000,000», «۴٫۵» or «4.5». NaN when nothing usable is typed. */
export const parseFaNumber = (input: string): number => {
    const ascii = input
        .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
        .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
        .replace(/[٫]/g, '.')
        .replace(/[,٬،\s]/g, '');
    if (!/^\d*\.?\d*$/.test(ascii) || ascii === '' || ascii === '.') return NaN;
    return Number(ascii);
};

/** Seconds of the Tehran midnight that starts the day containing `ms`. */
export const tehranDayStart = (ms: number): number => {
    const sec = Math.floor(ms / 1000);
    return Math.floor((sec + TEHRAN_OFFSET_SEC) / 86400) * 86400 - TEHRAN_OFFSET_SEC;
};

/** Is the Tehran gold market normally open at `ms`? */
export const isMarketHours = (ms: number): boolean => {
    const t = new Date(ms + TEHRAN_OFFSET_SEC * 1000);
    const hour = t.getUTCHours() + t.getUTCMinutes() / 60;
    return (
        !MARKET_HOURS.closedDays.includes(t.getUTCDay()) &&
        hour >= MARKET_HOURS.open &&
        hour < MARKET_HOURS.close
    );
};

const partsOf = (date: Date, options: Intl.DateTimeFormatOptions) =>
    Object.fromEntries(
        new Intl.DateTimeFormat('fa-IR-u-ca-persian', { timeZone: TEHRAN_TZ, ...options })
            .formatToParts(date)
            .map((p) => [p.type, p.value]),
    );

/** «سه‌شنبه ۵ آبان ۱۴۰۵» (withWeekday) or «۵ آبان ۱۴۰۵» */
export const jalaliDate = (date: Date, withWeekday = false): string => {
    const p = partsOf(date, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...(withWeekday ? { weekday: 'long' } : {}),
    });
    const core = `${p.day} ${p.month} ${p.year}`;
    return withWeekday ? `${p.weekday} ${core}` : core;
};

/** «۵ آبان» */
export const jalaliDayMonth = (date: Date): string => {
    const p = partsOf(date, { day: 'numeric', month: 'long' });
    return `${p.day} ${p.month}`;
};

/** «۲۱:۴۸:۱۹» or «۲۱:۴۸» in Tehran time */
export const tehranTime = (date: Date, withSeconds = true): string =>
    new Intl.DateTimeFormat('fa-IR', {
        timeZone: TEHRAN_TZ,
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds ? { second: '2-digit' } : {}),
        hour12: false,
    }).format(date);
